import { Link } from "@inertiajs/react"
import type { Listing } from "@/types"
import { Badge } from "@/components/ui/badge"
import { OptimizedImage } from "@/components/ui/optimized-image"
import { LISTING_TYPE_LABELS, STATUS_VARIANTS } from "./listing-constants"

function formatRelativeTime(dateStr: string | Date): string {
  const date = typeof dateStr === "string" ? new Date(dateStr) : dateStr
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return "just now"
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" })
}

export default function ListingCard({ listing }: { listing: Listing }) {
  const hasImage = listing.image_urls.length > 0

  return (
    <Link
      href={`/dashboard/listings/${listing.slug}`}
      className="group overflow-hidden rounded-xl border border-stone/40 bg-white transition-colors hover:border-red/30 hover:shadow-sm"
    >
      {/* Image */}
      <div className="aspect-[16/10] w-full overflow-hidden bg-stone/20">
        {hasImage ? (
          <OptimizedImage
            src={listing.image_urls[0]}
            alt={listing.address}
            className="size-full object-cover transition-transform group-hover:scale-105"
          />
        ) : (
          <div className="flex size-full items-center justify-center">
            <svg className="size-10 text-warm-grey/40" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
            </svg>
          </div>
        )}
      </div>

      {/* Details */}
      <div className="p-4">
        <div className="mb-2 flex items-center gap-2">
          <Badge variant={STATUS_VARIANTS[listing.status]} className="text-[10px] capitalize">
            {listing.status}
          </Badge>
          <Badge variant="outline" className="text-[10px]">
            {LISTING_TYPE_LABELS[listing.listing_type]}
          </Badge>
        </div>
        <h3 className="mb-1 text-sm font-medium text-charcoal line-clamp-1">
          {listing.address}
        </h3>
        {listing.price && (
          <p className="text-sm font-semibold text-charcoal">{listing.price}</p>
        )}
        <div className="mt-2 flex items-center gap-3 text-xs text-warm-grey">
          {listing.bedrooms != null && (
            <span>{listing.bedrooms} bed{listing.bedrooms !== 1 ? "s" : ""}</span>
          )}
          {listing.bathrooms != null && (
            <span>{listing.bathrooms} bath{listing.bathrooms !== 1 ? "s" : ""}</span>
          )}
          {listing.image_count > 0 && (
            <span>{listing.image_count} photo{listing.image_count !== 1 ? "s" : ""}</span>
          )}
          <span className="ml-auto">{formatRelativeTime(listing.updated_at)}</span>
        </div>
      </div>
    </Link>
  )
}
